// Pure helpers for the particle cursor canvas. Extracted so the React
// effect stays small and focused on lifecycle / event wiring.

export const INTERACTIVE_SELECTOR =
  "button, a, input, textarea, select, [role='button'], .btn-gold, .btn-ghost-gold";

export const MAX_PARTICLES = 90;
export const HALO_RADIUS = 48;
export const PARTICLE_HALO_FACTOR = 4.2;

export const isOverInteractive = (target) =>
  !!(target && typeof target.closest === "function" && target.closest(INTERACTIVE_SELECTOR));

/**
 * Create new particles along the cursor's last motion segment.
 * Mutates the supplied `particles` array in place and caps its length.
 */
export const spawnParticles = (particles, mouse, overInteractive) => {
  const dx = mouse.x - mouse.prevX;
  const dy = mouse.y - mouse.prevY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const baseCount = Math.min(2, Math.floor(dist / 14) + 1);
  const count = overInteractive ? Math.min(1, baseCount) : baseCount;

  for (let i = 0; i < count; i++) {
    const t = i / Math.max(1, count);
    particles.push({
      x: mouse.prevX + dx * t + (Math.random() - 0.5) * 3,
      y: mouse.prevY + dy * t + (Math.random() - 0.5) * 3,
      vx: (Math.random() - 0.5) * 0.22,
      vy: (Math.random() - 0.5) * 0.22 - 0.06,
      life: 1,
      decay: 0.038 + Math.random() * 0.018,
      size: Math.random() * 1.3 + 0.35,
    });
  }
  if (particles.length > MAX_PARTICLES) {
    particles.splice(0, particles.length - MAX_PARTICLES);
  }
};

export const drawCursorHalo = (ctx, mouse, overInteractive) => {
  if (mouse.x <= -50) return;
  const alpha = overInteractive ? 0.06 : 0.14;
  const grad = ctx.createRadialGradient(
    mouse.x, mouse.y, 0,
    mouse.x, mouse.y, HALO_RADIUS
  );
  grad.addColorStop(0, `rgba(243, 229, 171, ${alpha})`);
  grad.addColorStop(0.5, `rgba(212, 175, 55, ${alpha * 0.35})`);
  grad.addColorStop(1, "rgba(212, 175, 55, 0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(mouse.x, mouse.y, HALO_RADIUS, 0, Math.PI * 2);
  ctx.fill();
};

export const drawParticle = (ctx, p, dimFactor) => {
  const alpha = Math.max(0, p.life) * dimFactor;
  const r = p.size * (0.55 + p.life * 0.35);

  const halo = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * PARTICLE_HALO_FACTOR);
  halo.addColorStop(0, `rgba(255, 232, 170, ${alpha * 0.7})`);
  halo.addColorStop(0.4, `rgba(212, 175, 55, ${alpha * 0.28})`);
  halo.addColorStop(1, "rgba(212, 175, 55, 0)");
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(p.x, p.y, r * PARTICLE_HALO_FACTOR, 0, Math.PI * 2);
  ctx.fill();

  ctx.beginPath();
  ctx.fillStyle = `rgba(255, 245, 215, ${alpha * 0.85})`;
  ctx.arc(p.x, p.y, r * 0.5, 0, Math.PI * 2);
  ctx.fill();
};

/**
 * Advance the simulation by one frame and render to `ctx`.
 * Returns nothing; mutates `particles` (removes dead ones).
 */
export const stepAndRender = (ctx, canvas, particles, mouse, overInteractive) => {
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  drawCursorHalo(ctx, mouse, overInteractive);

  const dimFactor = overInteractive ? 0.35 : 1;
  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i];
    p.x += p.vx;
    p.y += p.vy;
    p.life -= p.decay;
    if (p.life <= 0) {
      particles.splice(i, 1);
      continue;
    }
    drawParticle(ctx, p, dimFactor);
  }
};
